import type { DataTableColumn } from '@sparklayer-io/spark-dash-ui/components/ui/data-table/types';
import { DataTable, Text } from '@ui/themed';
import { t } from 'i18next';
import { I18N_PAGE } from '../constants';
import type { Customer } from '../types';

function columns(): DataTableColumn<Customer>[] {
  return [
    {
      id: 'customer',
      label: t(`${I18N_PAGE}.search_table.headings.customer`),
      renderCell: (customer) => (
        <div className="flex flex-col">
          <Text bold truncate>
            {`${customer.first_name} ${customer.last_name}`.trim() || customer.email}
          </Text>
          <Text variant="secondary" truncate>
            {customer.email}
          </Text>
        </div>
      ),
    },
    {
      id: 'external_id',
      label: t(`${I18N_PAGE}.search_table.headings.external_id`),
      renderCell: (customer) => (
        <Text variant="secondary" truncate>
          {customer.external_id ?? ''}
        </Text>
      ),
    },
  ];
}

type CustomerSearchTableProps = {
  searchTerm: string;
  debouncedTerm: string;
  customer: Customer | undefined;
  isFetching: boolean;
  onSearchTermChange: (term: string) => void;
  onSelect: (customer: Customer) => void;
};

export function CustomerSearchTable({
  searchTerm,
  debouncedTerm,
  customer,
  isFetching,
  onSearchTermChange,
  onSelect,
}: CustomerSearchTableProps) {
  const hasTerm = searchTerm.trim().length > 0;
  const isDebouncing = searchTerm.trim() !== debouncedTerm.trim();
  const loading = hasTerm && (isDebouncing || isFetching);

  return (
    <DataTable<Customer>
      caption={t(`${I18N_PAGE}.search_table.caption`)}
      columns={columns()}
      data={customer ? [customer] : []}
      getRowId={(row) => row.id}
      search={{
        value: searchTerm,
        placeholder: t(`${I18N_PAGE}.search_placeholder`),
        onChange: onSearchTermChange,
      }}
      loading={loading}
      skeleton={{ rows: 1 }}
      onRowClick={onSelect}
    />
  );
}
